import React, { useCallback, useEffect, useState } from 'react';
import { P, H3, A } from '@osmosis/tailwind-ui/components/Text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { FormattedMessage, useIntl } from 'react-intl';
import { AnimatePresence } from 'motion/react';
import { LoggedInLibrarySidenavBlockContent } from '../../types/library';
import { LabelComponent } from './LabelComponent';
import { HEADER_LOGGED_IN_HEIGHT } from '../../styles/header';
import NewClinicalBanner from './NewClinicalBanner';
import { bgBagLightBlue, text5 } from '@osmosis/tailwind-ui/styles';
import { useDeviceType } from '@osmosis/nextjs-components/context/DeviceTypeProvider';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type Props = {
  content: LoggedInLibrarySidenavBlockContent[];
  hasNewContent?: boolean;
};

type PropsLibrarySidebarAccordion = {
  topic: LoggedInLibrarySidenavBlockContent;
  idx: number;
};

type LibraryL3Props = {
  topic: LoggedInLibrarySidenavBlockContent;
  idx: number;
};

//handles apostrophes in the url
const sanitizeUrlPath = (path: string) => path.replace(/'/g, '');

const LibraryL3 = ({ topic }: LibraryL3Props) => {
  const sanitizedLink = sanitizeUrlPath(topic.link);
  const { getClassValue } = useContentSize();

  const boxResponsiveClasses = getClassValue({
    defaultClasses: 'md:max-w-auto md:w-auto',
    breakpoints: {
      md: 'max-w-auto w-auto',
    },
  });

  const flexResponsiveClasses = getClassValue({
    defaultClasses: 'md:items-start',
    breakpoints: {
      md: 'items-start',
    },
  });

  const h3ResponsiveClasses = getClassValue({
    defaultClasses: 'md:w-100px lg:w-125px lg:text-base lg:normal-case',
    breakpoints: {
      md: 'w-100px',
      lg: 'w-125px text-base normal-case',
    },
  });

  return (
    <Box className={cn('w-[600px] max-w-[600px]', boxResponsiveClasses)}>
      <Flex
        data-testid="l3-container"
        className={cn(
          'gap-1.25 items-center justify-between',
          flexResponsiveClasses,
        )}
      >
        <A href={sanitizedLink} className="no-underline">
          <H3
            className={cn(
              text5,
              's:text-lg w-auto !text-base uppercase',
              h3ResponsiveClasses,
            )}
          >
            {topic.displayName}
          </H3>
        </A>
      </Flex>
    </Box>
  );
};

/**
 * LibrarySidebar.
 */
const LibrarySidebar = (props: Props) => {
  const { content, hasNewContent } = props;
  const stickyHeaderHeight = HEADER_LOGGED_IN_HEIGHT + 60;

  const { getClassValue } = useContentSize();

  const responsiveClasses = getClassValue({
    defaultClasses: `md:mb-0 md:w-[220px] lg:sticky lg:w-[260px] lg:top-${stickyHeaderHeight} md:max-h-[calc(100vh-65px)] lg:max-h-[calc(100vh-65px)]`,
    breakpoints: {
      md: `mb-0 w-[220px] max-h-[calc(100vh-65px)]`,
      lg: `mb-0 sticky w-[260px] top-${stickyHeaderHeight} max-h-[calc(100vh-65px)]`,
    },
  });

  return (
    <>
      {hasNewContent ? <NewClinicalBanner /> : null}
      <Box
        data-testid="library-sidebar-container"
        className={cn(
          bgBagLightBlue,
          'relative top-0 mb-3 w-full pb-3 max-h-auto overflow-auto',
          responsiveClasses,
        )}
      >
        <Box>
          {content.map((topic, i) => (
            <LibrarySidebarAccordion
              topic={topic}
              idx={i}
              key={topic.sectionID}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

const LibrarySidebarAccordion = (props: PropsLibrarySidebarAccordion) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const { topic, idx } = props;
  const { formatMessage } = useIntl();
  const { getClassValue } = useContentSize();

  const triggerId = `sidebar-accordion-trigger-${topic.sectionID}`;
  const panelId = `sidebar-accordion-panel-${topic.sectionID}`;

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleAnimationComplete = useCallback(() => {
    if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen]);

  const labelResponsiveClasses = getClassValue({
    defaultClasses: 'min-w-[65px] md:min-w-0 lg:min-w-0 ml-auto lg:ml-0',
    breakpoints: {
      md: 'min-w-0 ml-auto',
      lg: 'min-w-0 ml-0',
      base: 'min-w-[65px] ml-auto',
    },
  });

  const iconButtonResponsiveClasses = getClassValue({
    defaultClasses: 'mr-3 md:mr-0',
    breakpoints: {
      md: 'mr-0',
      base: 'mr-3',
    },
  });

  const topicItemDisplayNameResponsiveClasses = getClassValue({
    defaultClasses: 'sm:text-base text-sm',
    breakpoints: {
      sm: 'text-base',
      base: '!text-sm',
    },
  });

  const handleToggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const { isMobile } = useDeviceType();

  return (
    <div data-testid="library-sidebar-accordion-item">
      {!topic.contains.length && (
        <Box
          data-testid="library-sidebar-accordion-item-without-contains"
          className="mt-2 pl-3 pr-3 pt-3"
        >
          <Flex className="items-center justify-between">
            <LibraryL3 topic={topic} idx={idx} />
          </Flex>
        </Box>
      )}

      {!!topic.contains.length && (
        <Box
          data-testid="library-sidebar-accordion-item-with-contains"
          className="mt-2 overflow-hidden pl-3 pr-3 pt-3"
        >
          <Flex className="items-center justify-between">
            <LibraryL3 topic={topic} idx={idx} />
            <Flex className="items-center">
              {topic?.label === 'new' && (
                <LabelComponent
                  content={
                    <FormattedMessage
                      defaultMessage="NEW"
                      id="library.topics.list.item.tag.new"
                    />
                  }
                  dataTestId={`new-tag-${idx}`}
                  containerProps={{
                    className: labelResponsiveClasses,
                  }}
                />
              )}
              <IconButton
                id={triggerId}
                data-testid="library-sidebar-accordion-item-toggle-icon"
                onClick={handleToggleOpen}
                aria-expanded={isOpen}
                aria-controls={panelId}
                ariaLabel={
                  isOpen
                    ? formatMessage(
                        {
                          id: 'library.accordion-item.aria_label_collapse',
                          defaultMessage: 'Collapse {topicTitle} sections',
                        },
                        {
                          topicTitle: topic.displayName,
                        },
                      )
                    : formatMessage(
                        {
                          id: 'library.accordion-item.aria_label_expand',
                          defaultMessage: 'Expand {topicTitle} sections',
                        },
                        {
                          topicTitle: topic.displayName,
                        },
                      )
                }
                className={cn(
                  'ml-auto h-7 w-8 min-w-8 !bg-transparent',
                  iconButtonResponsiveClasses,
                  text5,
                )}
                icon={
                  <Icon size="xs" icon={isOpen ? faChevronUp : faChevronDown} />
                }
              />
            </Flex>
          </Flex>
          <AnimatePresence initial={false}>
            {isOpen || !isMobile ? (
              <MotionDiv
                id={panelId}
                className="overflow-hidden"
                role="region"
                aria-labelledby={triggerId}
                data-testid="library-sidebar-accordion-item-animated-div"
                style={{
                  display: !isOpen && !isVisible ? 'none' : undefined,
                }}
                initial={{ height: isOpen ? 0 : 'auto' }}
                animate={{ height: isOpen ? 'auto' : 0 }}
                exit={{ height: isOpen ? 'auto' : 0 }}
                transition={{ duration: 0.5 }}
                onAnimationComplete={handleAnimationComplete}
              >
                <ul className="ml-2 pt-1">
                  {topic.contains
                    .filter((topicItem) => topicItem.contains?.length)
                    .map((topicItem) => (
                      <li
                        key={topicItem.sectionID}
                        className="list-none pl-3 pt-3"
                      >
                        <A
                          href={sanitizeUrlPath(topicItem.link)}
                          className="no-underline"
                        >
                          <P
                            className={cn(
                              text5,
                              `mr-2 mt-2 font-normal duration-400 text-md transition-colors hover:text-blue-600`,
                              topicItemDisplayNameResponsiveClasses,
                            )}
                          >
                            {topicItem.displayName}
                          </P>
                        </A>
                      </li>
                    ))}
                </ul>
              </MotionDiv>
            ) : null}
          </AnimatePresence>
        </Box>
      )}
    </div>
  );
};

export default LibrarySidebar;
